import { createSlice } from '@reduxjs/toolkit';
import type { DbTask } from '@hub/common';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'features/store/store';

export type ActivityTaskState = {
  // Identifies which task the current data belongs to. `activityName` is unique
  // only within a participation, so both are needed across a participation
  // switch.
  participationId?: string;
  activityName?: string;
  docData?: DbTask;
  received: boolean;
};

const initialState: ActivityTaskState = {
  received: false,
};

const activityTaskSlice = createSlice({
  name: 'activityTask',
  initialState,
  reducers: {
    activityTaskSnapshotReceived: (
      _state,
      action: PayloadAction<{ participationId: string; task: DbTask }>
    ): ActivityTaskState => ({
      participationId: action.payload.participationId,
      activityName: action.payload.task.activityName,
      docData: action.payload.task,
      received: true,
    }),
    // An empty snapshot resolves the task to "received with no document" so the
    // page can show its not-found state. Indistinguishable from a transient
    // first-null snapshot, which self-corrects when the real snapshot arrives.
    activityTaskNotFound: (
      _state,
      action: PayloadAction<{ participationId: string; activityName: string }>
    ): ActivityTaskState => ({
      participationId: action.payload.participationId,
      activityName: action.payload.activityName,
      received: true,
    }),
    // Resets the gate so a previously-opened task's doc never leaks into the
    // next task (a same-name activity in another participation would otherwise
    // look current until its first snapshot arrives).
    activityTaskCleared: (): ActivityTaskState => initialState,
  },
});

export const {
  activityTaskSnapshotReceived,
  activityTaskNotFound,
  activityTaskCleared,
} = activityTaskSlice.actions;

export const activityTaskReducer = activityTaskSlice.reducer;

export const selectActivityTask = (state: RootState) => state.activityTask;

// The live task doc is trustworthy only when both keys match: an `activityName`
// is unique only within a participation, so a same-name task in another
// participation would otherwise read as current.
export const selectMatchedActivityTaskDocData = (
  state: RootState,
  participationId: string,
  activityName: string
): DbTask | undefined =>
  state.activityTask.participationId === participationId &&
  state.activityTask.activityName === activityName
    ? state.activityTask.docData
    : undefined;
